import { Box, Divider, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import EquipmentCard from '../components/EquipmentCard';
import { useHistory } from 'react-router-dom';
import ButtonComponent from '../components/ButtonComponent';
import CustomDialog from '../components/CustomDialog';
import { ASSIGNED, BUY, BUTTON_SECONDARY, BUTTON_WARNING, SUCCESS, TOAST_ERROR } from '../assets/constants/Constants';
import {HOME, LANDING, ASSIGNED_LOCKERS, MY_RENTED_PRODUCTS, LOCKER_OPENED} from '../assets/constants/PageList';
import { useTranslation } from 'react-i18next';
import { getAssignedLockerList, openLockerDoor, releaseLockerOfPickUpUser, fetchPaymentStatus, cancelDropOffTransaction } from '../api/api';
import ShowToast from '../components/ToastComponent';
import { getSecureItemFromSpecificStorage } from '../lib/BrowserStorageAccessMiddleware';
import { LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, LS_LOCKER_BANK_INFO } from '../assets/constants/BrowserStorageKeys';
import { getPathForNextPage, calculateTimeDifference, verifyUserProximity, getUnitLabelParams } from '../lib/Utils';
import CircularProgressLoader from '../components/CircularProgressLoader';

const MyRentedProductsPage = () => {
    const [loadingMessage, setLoadingMessage] = useState('Loading. Please wait.');
    const [isLoading, setIsLoading] = useState(false);
    const [userProximityError, setUserProximityError] = useState('');
    const [isUserProximityErrorModalVisible, setIsUserProximityErrorModalVisible] = useState(false);
    const [myRentedProductsList, setMyRentedProductsList] = useState([]);
    const assignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const isGeoFencingEnabled = assignmentSettings?.is_geofencing;
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [isEndRentalConfirmationModalVisible, setIsEndRentalConfirmationModalVisible] = useState(false);
    const [elapsedTimes, setElapsedTimes] = useState({});
    const history = useHistory();
    const {t} = useTranslation()
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const hasRunRef = useRef(false);

    useEffect(() => {
        if (!hasRunRef.current) {
            fetchEquipmentsList();
            hasRunRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (myRentedProductsList.length > 0) {
            const intervalId = setInterval(() => {
                updateElapsedTimes();
            }, 1000);

            return () => clearInterval(intervalId);
        }
    }, [myRentedProductsList]);

    const updateElapsedTimes = () => {
        const updatedElapsedTimes = { ...elapsedTimes };
        myRentedProductsList.forEach((equipment) => {
            const id = equipment?.product?.id;
            updatedElapsedTimes[id] = calculateTimeDifference(equipment?.assigned_date, 'elapsed');
        });
        setElapsedTimes(updatedElapsedTimes);
    };

    const fetchEquipmentsList = async () => {
        // Make the API call to fetch the equipments list and store the list in the following variable.
        setLoadingMessage("Getting  Equipments List");
        setIsLoading(true);

        try {
            const response = await getAssignedLockerList(assignmentSettings?.id)
            if(response?.status === SUCCESS){
                const finalEquipmentsList = await checkBuyProductsPaymentStatus(response?.data);
                setMyRentedProductsList(finalEquipmentsList);
                setIsLoading(false);
                setLoadingMessage('');
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            console.error("Error fetching equipments list: ", error);
            setIsLoading(false);
        }
    };

    const checkBuyProductsPaymentStatus = async (equipmentsList) => {
        const purchasedEquipments = equipmentsList?.filter((equipment) => equipment?.product?.product_type === BUY);

        if (!purchasedEquipments?.length) {
            return equipmentsList;
        }

        const releasedRefIds = [];

        await Promise.all(purchasedEquipments.map(async (equipment) => {
            const paymentRecord = equipment?.payment_records?.[equipment.payment_records.length - 1];

            if (!paymentRecord?.transaction_id || !paymentRecord?.payment_gateway) {
                return;
            }

            try {
                const fetchPaymentStatusResponse = await fetchPaymentStatus(paymentRecord.transaction_id, paymentRecord.payment_gateway);
                const isPaid = fetchPaymentStatusResponse?.data?.is_paid === true;

                if (isPaid && equipment?.is_already_opened_once === false) {
                    return;
                }

                const releaseLockerResponse = await cancelDropOffTransaction(equipment?.ref_id, 'Pending Locker Cancel by PWA');
                if (releaseLockerResponse?.status === SUCCESS) {
                    releasedRefIds.push(equipment?.ref_id);
                }
            } catch (error) {
                console.error("Error checking payment status / releasing locker for purchased product: ", error);
            }
        }));

        return releasedRefIds.length
            ? equipmentsList.filter((equipment) => !releasedRefIds.includes(equipment?.ref_id))
            : equipmentsList;
    };

    const isOpenLockerForPurchase = (equipment) => equipment?.product?.product_type === BUY && equipment?.status === ASSIGNED && equipment?.is_already_opened_once === false;

    const endRentalButtonHandler = (equipment) => {
        setSelectedEquipment(equipment);

        if (isOpenLockerForPurchase(equipment)) {
            openLockerAndProceedToEndProductRental(equipment);
            return;
        }

        setIsEndRentalConfirmationModalVisible(true);
    }

    const openLockerAndProceedToEndProductRental = async (equipmentToOpen = selectedEquipment) => {
        setIsEndRentalConfirmationModalVisible(false);
        setLoadingMessage(t('opening_locker_please_wait', getUnitLabelParams(equipmentToOpen?.locker_bank?.bank_type ?? lockerBankInfo?.LockerBankType, t)));
        setIsLoading(true);

        if (isGeoFencingEnabled) {
            const maxAllowedDistanceInMeters = assignmentSettings?.geofencing_radius;
            const isUserNearbyLockerBank = await verifyUserProximity({latitude: equipmentToOpen?.locker_bank?.latitude, longitude: equipmentToOpen?.locker_bank?.longitude}, maxAllowedDistanceInMeters);

            if ( !isUserNearbyLockerBank.isNearby ) {
                setLoadingMessage('');
                setIsLoading(false);
                setUserProximityError(isUserNearbyLockerBank.message);
                setIsUserProximityErrorModalVisible(true);

                return;
            }
        }

        const refId = equipmentToOpen?.ref_id;
        if (!refId) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await openLockerDoor(refId);
            if (response.status === SUCCESS) {
                if (equipmentToOpen?.product?.product_type === BUY) {
                    setLoadingMessage(t('lockerReleasing', getUnitLabelParams(equipmentToOpen?.locker_bank?.bank_type ?? lockerBankInfo?.LockerBankType, t)));
                    const releaseLockerResponse = await releaseLockerOfPickUpUser(refId);
                    if (releaseLockerResponse?.status !== SUCCESS) {
                        setIsLoading(false);
                        ShowToast(t(releaseLockerResponse?.message), TOAST_ERROR);
                        return;
                    }
                }

                history.push({ pathname: getPathForNextPage(MY_RENTED_PRODUCTS, LOCKER_OPENED), state: { lockerResponse: equipmentToOpen } });
            } else {
                setIsLoading(false);
                ShowToast(t(response?.message), TOAST_ERROR);
            }
        } catch (error) {
            console.error("Error opening locker: ", error);
            setIsLoading(false);
        }
    }

    const rentNewProductButtonHandler = () => {
        history.push(getPathForNextPage(MY_RENTED_PRODUCTS, HOME));
    }

    return (
        <Box className='my-rented-products-page'>
            <Box className='main-container'> 
                {
                isLoading ? 
                   <Box className="main">
                        <CircularProgressLoader message={loadingMessage} />
                    </Box>
                    :
                    myRentedProductsList?.length > 0 ? (
                    <Box className='main'>
                        {myRentedProductsList?.map((equipment) => (
                            <Box className='equipment-category' key={equipment?.product?.id}>
                                <EquipmentCard
                                    title={equipment.product?.title}
                                    description={equipment.product?.short_description}
                                    image={equipment?.product?.product_image_array?.[0]}
                                    isRentedByUser={!equipment.product?.availability && equipment?.status === ASSIGNED}
                                    elapsedTime={elapsedTimes[equipment?.product?.id] || '0.00'}
                                    productType={equipment.product?.product_type}
                                />
                                <ButtonComponent
                                    buttonId={`end-rental-products-button-${equipment?.product?.id}`}
                                    handleClick={() => endRentalButtonHandler(equipment)}
                                    title={isOpenLockerForPurchase(equipment)
                                        ? t('open_locker_button', getUnitLabelParams(equipment?.locker_bank?.bank_type ?? lockerBankInfo?.LockerBankType, t))
                                        : t('endRentalBtnText')}
                                    customClass='end-rental-and-return-button'
                                    variant='outlined'
                                    type={BUTTON_WARNING}
                                />
                                <Divider />
                            </Box>
                        ))}
                        <Box className='end-rental-and-rent-new-product-container'>
                            <ButtonComponent buttonId='rent-new-product-button' handleClick={rentNewProductButtonHandler} title={t('rentNewProductBtnText')} customClass='rent-new-product-button' type={BUTTON_SECONDARY} />
                        </Box>
                        <CustomDialog
                            dialogId='end-rental-confirmation-dialog'
                            dialogVisible={isEndRentalConfirmationModalVisible}
                            onClose={() => setIsEndRentalConfirmationModalVisible(false)}
                            handleCancel={() => setIsEndRentalConfirmationModalVisible(false)}
                            handleAccept={openLockerAndProceedToEndProductRental}
                            dialogTitle={t('confirm_end_rental')}
                            dialogContentText={t('are_you_sure_to_open_locker_and_end_rental')}
                            buttonOneTitle={t('button_cancel')}
                            buttonTwoTitle={t('yesBtnText')}
                            buttonProps={{buttonOne: {variant: 'outlined', customClass: 'cancel-button'}, buttonTwo: {type: BUTTON_WARNING}}}
                        />
                        <CustomDialog
                            dialogId='user-proximity-error-modal'
                            dialogVisible={isUserProximityErrorModalVisible}
                            onClose={() => setIsUserProximityErrorModalVisible(false)}
                            handleCancel={() => setIsUserProximityErrorModalVisible(false)}
                            dialogTitle={t('Location Error')}
                            dialogContentText={userProximityError}
                            buttonOneTitle={t('Ok')}
                            buttonProps={{buttonOne: {type: BUTTON_WARNING}}}
                        />
                    </Box>
                    ) :(
                        <Box className='main'>
                            <Typography className='assignText'>{t('notAssignLockerText', getUnitLabelParams(lockerBankInfo?.LockerBankType, t))}</Typography>
                        </Box>
                    )
                }
            </Box>
        </Box>
    )
}

export default MyRentedProductsPage